// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { AsyncPipe, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  Input,
  OnChanges,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { map } from 'rxjs';
import { ClipboardService } from 'src/app/modules/shared/services/clipboard.service';
import {
  OperationalState,
  OperationalStateColor,
} from '../../../../_interfaces/service-ticket-model';
import { APICallGateComponent } from '../../../shared/components/api-call-gate.component';
import { LocalizedDatePipe } from '../../../shared/pipes/localizedDate.pipe';
import { ScreenLayoutService } from '../../../shared/services/screen-layout-service/screen-layout.service';
import { ServiceTicketsService } from '../../services/service-tickets.service';
import { isServiceTicket } from '../service-tickets-page/service-tickets.shared';

const topTicketCount = 3;

@Component({
  selector: 'app-service-tickets-panel',
  templateUrl: './service-tickets-panel.component.html',
  styleUrls: [
    './service-tickets-panel.component.scss',
    '../service-tickets-page/service-tickets-shared.scss',
  ],
  standalone: true,

  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    APICallGateComponent,
    NgTemplateOutlet,
    NgIf,
    NgFor,
    LocalizedDatePipe,
    AsyncPipe,
    TranslateModule,
  ],
})
export class ServiceTicketsPanelComponent implements OnChanges {
  @Input({ required: true }) systemMaterialNumber!: number;
  @Input({ required: true }) systemSerialNumber!: string;
  protected allTickets$ =
    this.serviceTicketsService.getTickets.lastSuccessfulData$.pipe(
      // filter for planned activities if needed
      map((tickets) => {
        return tickets.filter(isServiceTicket);
      })
    );

  topTickets$ = this.allTickets$.pipe(
    // pick top tickets
    map((tickets) =>
      tickets.slice(0, topTicketCount).map((ticket) => ({
        ...ticket,
        statusColor: statusColor(ticket.operationalState),
      }))
    )
  );

  ticketCountDisplay$ = this.allTickets$.pipe(
    map((tickets) => {
      if (tickets.length > 0) {
        return tickets.length > topTicketCount
          ? '(3/' + tickets.length + ')'
          : '(' + tickets.length + '/' + tickets.length + ')';
      } else {
        return '';
      }
    })
  );

  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected serviceTicketsService: ServiceTicketsService,
    protected screenLayoutService: ScreenLayoutService,
    protected clipboard: ClipboardService
  ) {}

  ngOnChanges(): void {
    // got to be system props
    this.serviceTicketsService.getTickets.request({
      systemMaterialNumber: this.systemMaterialNumber,
      systemSerialNumber: this.systemSerialNumber,
    });
  }

  isOverflowing(el: HTMLElement): boolean {
    if (!el.style.overflow || el.style.overflow === 'visible') {
      el.style.overflow = 'hidden';
    }
    return el.clientWidth < el.scrollWidth || el.clientHeight < el.scrollHeight;
  }

  loadDetailedView(): void {
    this.router.navigate(['tickets'], { relativeTo: this.route });
  }
}

export function statusColor(status: Readonly<OperationalState> | null): string {
  let color = '';
  switch (status) {
    case OperationalState.NoImpact:
      color = OperationalStateColor.Green;
      break;
    case OperationalState.PartiallyDown:
      color = OperationalStateColor.Yellow;
      break;
    case OperationalState.TotalDown:
      color = OperationalStateColor.Red;
      break;
    case null:
      color = OperationalStateColor.Grey;
      break;
  }

  return color;
}
